import React, { useState, useEffect } from 'react'
import Layout from '../components/Layout'
import ContractDateInput from '../components/ContractDateInput' // contract-date-be-20261016
import api from '../services/api'
import { useNavigate } from 'react-router-dom'
const { findOverlap, describeContract, thaiDate } = require('../utils/employeeContractStatus')
const { nextContractStartDate, defaultContractEndDate, contractYears } = require('../utils/contractDates')

export default function CreateContractPage() {
  const [divisions,setDivisions]=useState([])
  const [divisionId,setDivisionId]=useState('')
  const [divisionEmployees,setDivisionEmployees]=useState([])
  const [searchEmployees,setSearchEmployees]=useState([])
  const [employeeContractStatuses,setEmployeeContractStatuses]=useState({})
  const [statusLoading,setStatusLoading]=useState(false)
  const [statusError,setStatusError]=useState(false)
  const [statusReload,setStatusReload]=useState(0)
  const [divisionLoading,setDivisionLoading]=useState(false)
  const [searchLoading,setSearchLoading]=useState(false)
  const [sendMessage,setSendMessage]=useState('')
  const loadVersionRef=React.useRef(0)
  const selectionRef=React.useRef(null)
  const requestKey=React.useRef(null)
  const [searchEmp, setSearchEmp] = useState('')
  const [selectedEmp, setSelectedEmp] = useState(null)
  const [renewalCheck, setRenewalCheck] = useState(null)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(null)
  const [skipEval, setSkipEval] = useState(false)
  const [skipReason, setSkipReason] = useState('')
  const navigate = useNavigate()

  const searchParams = new URLSearchParams(window.location.search)
  const renewId = searchParams.get('renew')
  const renewEmployeeId = searchParams.get('employee_id')
  const renewSalary = searchParams.get('salary')
  const renewAllowance = searchParams.get('allowance')
  const renewEndDate = searchParams.get('end_date')

  const calcNewStartDate = (endDate) => {
    return nextContractStartDate(endDate)
  }

  const newStartDate = renewEndDate ? calcNewStartDate(renewEndDate) : ''

  const [form, setForm] = useState({
    contract_no: '',
    contract_type: renewId ? 'ต่อสัญญา' : 'สัญญาใหม่',
    start_date: newStartDate,
    end_date: '',
    salary: renewSalary || '',
    allowance: renewAllowance || '0',
  })

  useEffect(()=>{api.get('/divisions').then(res=>setDivisions(res.data.data)).catch(()=>setError('โหลดกองงานไม่สำเร็จ'))},[])
  useEffect(()=>{
    let active=true
    setDivisionEmployees([])
    loadVersionRef.current++;setLoading(false);setDivisionLoading(false)
    if(!divisionId) return
    setDivisionLoading(true)
    ;(async()=>{
      try {
        let page=1,all=[],last=1
        do {const res=await api.get('/employees',{params:{division_id:divisionId,status:'active',page,limit:100}});all.push(...res.data.data);last=res.data.pagination.totalPages;page++}while(page<=last)
        if(active)setDivisionEmployees(all)
      }catch(e){if(active)setError('โหลดรายชื่อพนักงานไม่สำเร็จ')}
      finally{if(active)setDivisionLoading(false)}
    })()
    return()=>{active=false}
  },[divisionId])
  useEffect(()=>{
    let active=true
    const query=searchEmp.trim()
    if(divisionId || query.length<2){setSearchEmployees([]);setSearchLoading(false);return()=>{active=false}}
    setSearchLoading(true)
    const timer=window.setTimeout(async()=>{
      try{
        const res=await api.get('/employees',{params:{search:query,status:'active',page:1,limit:100}})
        if(active)setSearchEmployees(res.data?.data || [])
      }catch(e){if(active){setSearchEmployees([]);setError('ค้นหารายชื่อพนักงานไม่สำเร็จ กรุณาลองใหม่')}}
      finally{if(active)setSearchLoading(false)}
    },250)
    return()=>{active=false;window.clearTimeout(timer)}
  },[divisionId,searchEmp])
  // Batch status lookup for the existing employee list; no changes to selection/buttons.
  useEffect(() => {
    const employees = divisionId ? divisionEmployees : searchEmployees
    const ids = [...new Set(employees.map(emp => emp.id).filter(Boolean))]
    let active = true
    setEmployeeContractStatuses({})
    setStatusError(false)
    if (!ids.length) { setStatusLoading(false); return () => { active = false } }
    setStatusLoading(true)
    ;(async () => {
      try {
        const batches = []
        // Keep UUID query strings below IIS's default limit, even after URL encoding.
        const batchSize = 25
        for (let i = 0; i < ids.length; i += batchSize) batches.push(ids.slice(i, i + batchSize))
        const results = await Promise.all(batches.map(batch => api.get('/contracts/employee-status-summary', { params: { employee_ids: batch.join(',') } })))
        if (results.some(r => !r.data?.success)) throw new Error('Status lookup failed')
        if (active) setEmployeeContractStatuses(Object.assign({}, ...results.map(r => r.data?.data || {})))
      } catch (err) {
        if (active) { setEmployeeContractStatuses({}); setStatusError(true) }
      } finally { if (active) setStatusLoading(false) }
    })()
    return () => { active = false }
  }, [divisionId, divisionEmployees, searchEmployees, statusReload])

  const contractStatusBadge = (emp) => {
    if (statusLoading) return <span className="employee-contract-badge checking">กำลังตรวจสอบ…</span>
    if (statusError) return <span className="employee-contract-badge inactive">ตรวจสอบสถานะไม่สำเร็จ</span>
    const summary = employeeContractStatuses[emp.id]
    const overlap = findOverlap(summary, form.start_date, form.end_date)
    const contract = overlap || summary
    const badge = describeContract(contract)
    return <div className="employee-contract-summary">
      <span className={'employee-contract-badge ' + badge.tone}>{badge.label}</span>
      {contract && <small>เลขสัญญา {contract.contract_no} · {thaiDate(contract.start_date)} – {thaiDate(contract.end_date)}</small>}
      {form.start_date && form.end_date && !statusError && <small className={overlap ? 'period-blocked' : 'period-clear'}>{overlap ? 'มีสัญญาในช่วงวันที่ที่เลือกแล้ว' : 'ยังไม่มีสัญญาในช่วงวันที่ที่เลือก'}</small>}
      {contract?.id && <button type="button" className="text-button contract-open" onClick={()=>navigate(`/contracts/${contract.id}`)}>เปิดสัญญาเดิม</button>}
    </div>
  }

  const sendSigningEmail=async()=>{
    setSaving(true);setSendMessage('')
    try{const res=await api.post(`/contracts/${success.data.id}/send-qr-email`);setSendMessage(res.data.message);setSuccess({...success,data:{...success.data,status:'pending'}})}
    catch(e){setSendMessage(e.response?.data?.message || 'ส่งอีเมลไม่สำเร็จ กรุณาลองใหม่')}
    finally{setSaving(false)}
  }
  useEffect(() => {
    if (renewEmployeeId) {
      const loadEmployee = async () => {
        try {
          const res = await api.get(`/employees/${renewEmployeeId}`)
          if (res.data.success) handleSelectEmp(res.data.data)
        } catch (err) { console.error(err) }
      }
      loadEmployee()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const checkRenewal = async (empId, selectedType) => {
    const version=++loadVersionRef.current
    try {
      setLoading(true)
      const res = await api.get(`/contracts/check-renewal/${empId}`)
      if (res.data.success && selectionRef.current===empId && version===loadVersionRef.current) {
        setRenewalCheck(res.data)
        const old=res.data.previousContract
        setForm(f=>{const start=selectedType==='ต่อสัญญา' && old?.end_date ? calcNewStartDate(old.end_date) : f.start_date;return {...f,salary:selectedType==='ต่อสัญญา' ? old?.salary ?? res.data.employee.salary ?? '' : res.data.employee.salary ?? '',allowance:old?.allowance ?? '0',start_date:start,end_date:start?calcEndDate(start,res.data.employee.emp_type):f.end_date}})
      }
    } catch (err) { if(version===loadVersionRef.current)setError('โหลดข้อมูลพนักงานและสัญญาเดิมไม่สำเร็จ กรุณาเลือกพนักงานอีกครั้ง') }
    finally { if(selectionRef.current===empId && version===loadVersionRef.current)setLoading(false) }
  }

  const handleSelectEmp = (emp, type) => {
    selectionRef.current=emp.id
    requestKey.current=Array.from(window.crypto.getRandomValues(new Uint8Array(16)),b=>b.toString(16).padStart(2,'0')).join('')
    setRenewalCheck(null)
    setForm(f=>({...f,contract_type:type || f.contract_type,salary:'',allowance:'0',start_date:'',end_date:''}))
    setSelectedEmp(emp)
    if(!divisionId && emp.division_id)setDivisionId(emp.division_id)
    setSearchEmp('')
    setSkipEval(false)
    setSkipReason('')
    checkRenewal(emp.id, type || form.contract_type)
  }

  const calcEndDate = (startDate, empType) => {
    return defaultContractEndDate(startDate, empType)
  }

  const handleStartDateChange = (date) => {
    const endDate = selectedEmp ? calcEndDate(date, selectedEmp.emp_type) : ''
    setForm({ ...form, start_date: date, end_date: endDate })
  }

  const totalSalary = (parseFloat(form.salary) || 0) + (parseFloat(form.allowance) || 0)

  const normalizeSearch = value => String(value || '').normalize('NFC').replace(/\s+/g, '').toLocaleLowerCase('th-TH')
  const employeeSource = divisionId ? divisionEmployees : searchEmployees
  const visibleEmployees = employeeSource.filter(employee => {
    const query = normalizeSearch(searchEmp)
    if (!query) return true
    return normalizeSearch(`${employee.prefix || ''}${employee.first_name || ''}${employee.last_name || ''}${employee.position || ''}${employee.id_card || ''}`).includes(query)
  })

  const showFormError = message => {
    setError(message)
    window.setTimeout(()=>document.querySelector('.create-page .form-actions')?.scrollIntoView({behavior:'smooth',block:'center'}),0)
  }

  const handleSubmit = async () => {
    setError('')
    if (saving) return
    if (statusLoading || statusError) { showFormError('กรุณารอให้ตรวจสอบสถานะสำเร็จ หรือลองโหลดสถานะใหม่'); return }
    const overlappingContract = selectedEmp && findOverlap(employeeContractStatuses[selectedEmp.id], form.start_date, form.end_date)
    if (overlappingContract) { showFormError(`ช่วงวันที่นี้มีสัญญา ${overlappingContract.contract_no} แล้ว กรุณาเปิดสัญญาเดิม`); return }
    if (!selectedEmp) { showFormError('กรุณาค้นหาและเลือกพนักงานก่อนสร้างสัญญา'); return }
    if (loading) { showFormError('ระบบกำลังตรวจสอบข้อมูลพนักงาน กรุณารอสักครู่แล้วลองอีกครั้ง'); return }
    if (!renewalCheck) { showFormError('ยังตรวจสอบข้อมูลพนักงานไม่สำเร็จ กรุณาเลือกพนักงานอีกครั้ง'); return }
    if (!form.start_date || !form.end_date || !form.salary) { showFormError('กรุณากรอกวันที่เริ่ม วันที่สิ้นสุด และเงินเดือนให้ครบถ้วน'); return }
    if (form.end_date <= form.start_date) { showFormError('วันที่สิ้นสุดสัญญาต้องอยู่หลังวันที่เริ่มสัญญา'); return }
    try {
      setSaving(true)
      const { contract_no: ignoredContractNo, ...contractData } = form
      const payload = { ...contractData, save_as_draft:true, employee_id: selectedEmp.id, salary: parseFloat(form.salary), allowance: parseFloat(form.allowance) || 0, skip_evaluation: skipEval }
      if (skipEval) payload.skip_reason = skipReason.trim()
      const res = await api.post('/contracts', payload, {headers:{'Idempotency-Key':requestKey.current}})
      if (res.data.success) {
        setSuccess(res.data)
        window.dispatchEvent(new Event('notifications:refresh'))
      }
    } catch (err) { showFormError(err.response?.data?.message || 'สร้างสัญญาไม่สำเร็จ กรุณาลองใหม่') }
    finally { setSaving(false) }
  }

  if (success) {
    return (
      <Layout>
        <div style={{ maxWidth: '480px', margin: '80px auto', textAlign: 'center' }}>
          <div style={{ width: '64px', height: '64px', background: '#f0fdf4', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
            <i className="ti ti-circle-check" style={{ fontSize: '32px', color: '#22c55e' }} />
          </div>
          <h2 style={{ fontSize: '20px', fontWeight: '500', color: '#0f172a', marginBottom: '8px' }}>สร้างสัญญาสำเร็จแล้ว</h2>
          <p style={{ fontSize: '13px', color: '#94a3b8', marginBottom: '24px' }}>เลขสัญญา {success.data.contract_no}</p>
          <p role="status">{sendMessage}</p>
          <button disabled={saving || success.data.status==='pending'} onClick={sendSigningEmail} style={{padding:12,background:'#075dcc',color:'white',borderRadius:8,marginBottom:16}}>{saving?'กำลังส่ง...':success.data.status==='pending'?'ส่งอีเมลแล้ว':'ส่ง E-mail ให้พนักงานลงนามภายใน 15 วัน'}</button>
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
            <button disabled={success.data.status==='draft'} onClick={() => navigate(`/contracts/${success.data.id}/qr`)}
              style={{ padding: '10px 24px', background: '#075dcc', color: 'white', border: 'none', borderRadius: '10px', fontSize: '13px', fontWeight: '500', cursor: 'pointer' }}>
              พิมพ์ QR Code
            </button>
            <button onClick={() => navigate('/contracts')}
              style={{ padding: '10px 24px', background: 'white', color: '#64748b', border: '0.5px solid #e2e8f0', borderRadius: '10px', fontSize: '13px', cursor: 'pointer' }}>
              ดูรายการสัญญา
            </button>
          </div>
        </div>
      </Layout>
    )
  }

  return <Layout><div className="refresh-page create-page">
    <header className="page-heading"><div className="create-heading-icon" aria-hidden="true"><i className="ti ti-file-plus" /></div><div><span className="eyebrow">จัดทำเอกสารสัญญา</span><h1>สร้างสัญญาจ้างพนักงาน</h1><p>เลือกพนักงาน กำหนดรายละเอียด และตรวจสอบก่อนบันทึก</p></div></header>
    <ol className="create-progress" aria-label="ขั้นตอนสร้างสัญญา">
      <li className="is-current"><span>1</span><div><strong>เลือกพนักงาน</strong><small>ค้นหาจากกองงานหรือชื่อ</small></div></li>
      <li className={selectedEmp?'is-current':''}><span>2</span><div><strong>รายละเอียดสัญญา</strong><small>กำหนดวันและค่าตอบแทน</small></div></li>
      <li className={selectedEmp&&form.start_date&&form.end_date&&form.salary?'is-current':''}><span>3</span><div><strong>ตรวจสอบและบันทึก</strong><small>ออกเลขสัญญาอัตโนมัติ</small></div></li>
    </ol>
    {error&&<p className="notice warning" role="alert">{error}</p>}
    <div className="create-columns"><div className="create-form">
      <section className="surface"><div className="create-section-title"><span className="step-number">1</span><div><h2>เลือกกองงานและพนักงาน</h2><p>เริ่มจากกองงาน หรือค้นหาชื่อพนักงานโดยตรง</p></div></div>
        <div className="employee-search-panel"><div className="field-grid"><label>กองงาน<select aria-label="กองงาน" value={divisionId} onChange={e=>{setSelectedEmp(null);selectionRef.current=null;setDivisionId(e.target.value)}}><option value="">เลือกกองงาน</option>{divisions.map(d=><option key={d.id} value={d.id}>{d.name}</option>)}</select></label><label>ค้นหาพนักงาน<input aria-label="ค้นหาพนักงาน" value={searchEmp} onChange={e=>setSearchEmp(e.target.value)} placeholder="ค้นหาชื่อหรือนามสกุล"/></label></div></div>
        {(divisionId||searchEmp.trim().length>=2)&&!divisionLoading&&!searchLoading&&<p className="employee-count">พบพนักงาน {visibleEmployees.length} คน</p>}
        {statusError&&<div className="notice warning" role="alert">โหลดสถานะสัญญาไม่สำเร็จ กรุณาตรวจสอบก่อนสร้างสัญญา <button type="button" onClick={()=>setStatusReload(n=>n+1)}>ลองโหลดสถานะใหม่</button></div>}
        <div className="employee-options">{divisionLoading||searchLoading?<p className="empty-state"><i className="ti ti-loader-2" aria-hidden="true"/>กำลังค้นหารายชื่อ…</p>:!divisionId&&searchEmp.trim().length<2?<p className="empty-state"><i className="ti ti-user-search" aria-hidden="true"/>เลือกกองงาน หรือพิมพ์ชื่อ/นามสกุลอย่างน้อย 2 ตัวอักษร</p>:visibleEmployees.map(emp=><div key={emp.id} className={'employee-option '+(selectedEmp?.id===emp.id?'selected':'')}><span className="person-icon"><i className="ti ti-user"/></span><div><strong>{emp.prefix}{emp.first_name} {emp.last_name}</strong>{contractStatusBadge(emp)}<small>{emp.position} · {emp.division_name || 'ไม่ระบุกองงาน'}</small></div><div className="row-actions"><button type="button" className="btn outline compact" onClick={()=>handleSelectEmp(emp,'สัญญาใหม่')}>สร้างสัญญา</button><button type="button" className="btn outline compact" onClick={()=>handleSelectEmp(emp,'ต่อสัญญา')}>ต่อสัญญา</button></div></div>)}</div>
        {!divisionLoading&&!searchLoading&&(divisionId||searchEmp.trim().length>=2)&&visibleEmployees.length===0&&<p className="empty-state">ไม่พบพนักงานที่ยังใช้งานอยู่ กรุณาตรวจสอบชื่อ กองงาน หรือสถานะพนักงาน</p>}
        {selectedEmp&&<div className="notice blue"><i className="ti ti-lock"/><div><strong>พนักงานที่เลือก: {selectedEmp.prefix}{selectedEmp.first_name} {selectedEmp.last_name}</strong><p>{selectedEmp.position} · {selectedEmp.emp_type}</p></div></div>}
      </section>
      <section className="surface"><div className="create-section-title"><span className="step-number">2</span><div><h2>รายละเอียดสัญญา</h2><p>ตรวจสอบประเภทสัญญาและระยะเวลาที่ต้องการ</p></div></div>
        <div className="segments" aria-label="ประเภทสัญญา">{['สัญญาใหม่','ต่อสัญญา'].map(type=><button key={type} aria-pressed={form.contract_type===type} className={form.contract_type===type?'selected':''} onClick={()=>selectedEmp?handleSelectEmp(selectedEmp,type):setForm({...form,contract_type:type})}>{type}</button>)}</div>
        <div className="field-grid"><label>เลขสัญญา<input aria-label="เลขสัญญา" value="ระบบกำหนดอัตโนมัติเมื่อบันทึก" readOnly disabled title="ระบบออกเลขตามปี พ.ศ. และล็อกการออกเลขเพื่อป้องกันเลขซ้ำ"/></label><div/>
          <label>ระยะเวลาที่ระบบคำนวณ<input aria-label="ระยะเวลาที่ระบบคำนวณ" readOnly value={selectedEmp ? (contractYears(selectedEmp.emp_type) ? contractYears(selectedEmp.emp_type) + ' ปี' : 'ระบุวันที่เองสำหรับประเภทนี้') : 'เลือกพนักงานก่อน'} /><small>กำหนดตามประเภทพนักงาน เปลี่ยนวันเริ่มเพื่อคำนวณใหม่ และแก้วันสิ้นสุดได้</small></label>
          <label>วันที่เริ่มสัญญา (วว/ดด/ปปปป พ.ศ.)<ContractDateInput key={`${selectedEmp?.id}-start`} aria-label="วันที่เริ่มสัญญา" disabled={loading || !selectedEmp} value={form.start_date} onChange={handleStartDateChange}/></label>
          <label>วันที่สิ้นสุดสัญญา (วว/ดด/ปปปป พ.ศ.)<ContractDateInput key={`${selectedEmp?.id}-end`} aria-label="วันที่สิ้นสุดสัญญา" disabled={loading || !selectedEmp} value={form.end_date} onChange={date=>setForm({...form,end_date:date})}/></label>
          <label>เงินเดือน (บาท / เดือน)<input aria-label="เงินเดือน" type="number" min="0.01" step="0.01" value={form.salary} onChange={e=>setForm({...form,salary:e.target.value})}/></label>
          <label>เงินเพิ่มค่าครองชีพ (บาท)<input aria-label="เงินเพิ่มค่าครองชีพ" type="number" min="0" step="0.01" value={form.allowance} onChange={e=>setForm({...form,allowance:e.target.value})}/></label>
        </div>
        {selectedEmp&&findOverlap(employeeContractStatuses[selectedEmp.id],form.start_date,form.end_date)&&<div className="notice warning" role="alert">มีสัญญาในช่วงวันที่นี้แล้ว กรุณาเปิดสัญญาเดิม หรือเลือกช่วงวันที่ใหม่ที่ไม่ซ้อนกัน</div>}
        {loading&&<p role="status">กำลังตรวจสอบข้อมูลสัญญาเดิม…</p>}
        {form.contract_type==='ต่อสัญญา'&&renewalCheck&&!renewalCheck.canRenew&&<div className="notice warning"><div><p>{renewalCheck.message || 'ยังไม่ผ่านเงื่อนไขประเมินการต่อสัญญา'}</p><label className="consent-choice"><input type="checkbox" checked={skipEval} onChange={e=>setSkipEval(e.target.checked)}/>ข้ามการตรวจผลประเมินพร้อมระบุเหตุผล</label>{skipEval&&<textarea aria-label="เหตุผลข้ามการประเมิน" value={skipReason} onChange={e=>setSkipReason(e.target.value)} placeholder="ระบุเหตุผล"/>}</div></div>}
      </section>
    </div><aside className="surface contract-summary"><div className="summary-heading"><span className="summary-heading-icon"><i className="ti ti-file-description"/></span><div><h2>สรุปก่อนบันทึก</h2><p>ข้อมูลจะปรับตามรายการที่เลือก</p></div></div><div className="summary-person"><span className="person-icon"><i className="ti ti-user"/></span><strong>{selectedEmp?`${selectedEmp.prefix}${selectedEmp.first_name} ${selectedEmp.last_name}`:'ยังไม่ได้เลือกพนักงาน'}</strong><p>{selectedEmp?.position || 'เลือกพนักงานจากรายการด้านซ้าย'}</p></div><div className="summary-meta"><span>{selectedEmp?.division_name || divisions.find(d=>d.id===divisionId)?.name || 'ยังไม่ระบุกองงาน'}</span><span>{form.contract_type}</span></div><div className="summary-dates"><small>ช่วงเวลาสัญญา</small><p>{thaiDate(form.start_date)} ถึง {thaiDate(form.end_date)}</p></div><div className="money-row"><span>เงินเดือน</span><strong>{Number(form.salary || 0).toLocaleString('th-TH')} บาท</strong></div><div className="money-row"><span>ค่าครองชีพ</span><strong>{Number(form.allowance || 0).toLocaleString('th-TH')} บาท</strong></div><div className="summary-total"><small>รวมทั้งสิ้น</small><strong>{totalSalary.toLocaleString('th-TH')}</strong><span>บาท / เดือน</span></div><p className="summary-help">หลังบันทึก HR สามารถส่งอีเมลให้พนักงานลงนามได้ โดยกำหนดลงนามภายใน 15 วันหลังส่งอีเมล</p></aside></div>
    <footer className="form-actions"><button type="button" className="text-button" onClick={()=>navigate('/contracts')}>← ย้อนกลับ</button><div><span>บันทึกเป็นร่างก่อนส่งอีเมล</span>{error&&<p role="alert" style={{color:'#dc2626',fontSize:'12px',fontWeight:'600',marginTop:'4px',maxWidth:'440px'}}><i className="ti ti-alert-circle"/> {error}</p>}</div><button type="button" className="btn primary" onClick={handleSubmit} disabled={saving||statusLoading||statusError||!!(selectedEmp&&findOverlap(employeeContractStatuses[selectedEmp.id],form.start_date,form.end_date))||(form.contract_type==='ต่อสัญญา'&&renewalCheck&&!renewalCheck.canRenew&&(!skipEval||!skipReason.trim()))}>{saving?'กำลังบันทึก…':'สร้างสัญญา'}</button></footer>
  </div></Layout>
}
